/**
 * نشان‌شده‌ها بالای فهرستِ کشف — ولی نه در همون لحظه‌ای که قلب زده می‌شه.
 *
 * خواسته‌ی کاربر دقیقاً همینه: «درسته که وقتی لایک می‌کنم باید بره بالا، اما
 * نه همون لحظه». کارتی که زیرِ انگشتِ آدم می‌پره بالای فهرست، یعنی چیزی که
 * داشتی نگاهش می‌کردی گم شد و بقیه‌ی فهرست هم جابه‌جا شد.
 *
 * پس ترتیب از یه **عکسِ لحظه‌ای** (snapshot) از مجموعه‌ی نشان‌شده‌ها می‌خونه،
 * نه از مجموعه‌ی زنده. خودِ قلب همون لحظه پر و خالی می‌شه (اون از مجموعه‌ی
 * زنده می‌آد)؛ فقط **ترتیب** ثابت می‌مونه تا کاربر دوباره وارد صفحه بشه یا
 * دستی رفرش کنه.
 *
 * چرا اینجا و نه توی صفحه: دو تا صفحه هست (گوشی و سایت) و قبلاً فقط یکیشون
 * نشان‌شده‌ها رو بالا می‌آورد. یه پیاده‌سازی، یه رفتار، و قابلِ تست بدونِ
 * رندر کردنِ صفحه.
 */
import { useRef } from 'react';

/** همون دو نوعی که جدولِ favorites می‌شناسه. */
export type FavoriteKind = 'event' | 'business';

/** مجموعه‌ی کلیدهای نشان‌شده. */
export type FavoriteSnapshot = ReadonlySet<string>;

/** عکسِ خالی — تا وقتی که هنوز چیزی از سرور نیومده. */
export const NO_FAVORITES: FavoriteSnapshot = new Set<string>();

/**
 * کلیدِ یه نشان‌شده.
 *
 * فقط شناسه کافی نیست: رخداد و مکان دو جدولِ جدان و هیچ‌چی تضمین نمی‌کنه
 * شناسه‌هاشون به هم نخورن — همون قاعده‌ای که `rowKey` توی صفحه‌ی کشف داره.
 */
export const favoriteKey = (kind: FavoriteKind, id: string): string => `${kind}-${id}`;

/** خروجیِ `favoriteRepo.list` رو به مجموعه‌ی کلید تبدیل می‌کنه. */
export function favoriteKeys(
  rows: readonly { kind: FavoriteKind; refId: string }[],
): FavoriteSnapshot {
  return new Set(rows.map((f) => favoriteKey(f.kind, f.refId)));
}

/**
 * نشان‌شده‌ها اول، بقیه بعد — بدونِ به‌هم زدنِ ترتیبِ داخلِ هر دسته.
 *
 * ترتیبِ صدا زدن مهمه: اول `sortItems`، بعد این. برعکسش، مرتب‌سازی
 * نشان‌شده‌ها رو دوباره قاطیِ بقیه می‌کرد.
 */
export function favoritesFirst<T>(
  rows: readonly T[], snapshot: FavoriteSnapshot, keyOf: (row: T) => string,
): T[] {
  const marked: T[] = [];
  const rest: T[] = [];
  for (const row of rows) (snapshot.has(keyOf(row)) ? marked : rest).push(row);
  return [...marked, ...rest];
}

/**
 * حالتِ «منجمد»: خودِ عکس، آخرین نشانه‌ی رفرش، و اینکه منتظرِ داده‌ی تازه‌ایم.
 *
 * `armed` یعنی «اجازه داری دفعه‌ی بعد که داده نشست، عکسِ تازه بگیری». همین یه
 * پرچم فاصله‌ی بینِ «کاربر رفرش زد» و «داده‌ی تازه رسید» رو پر می‌کنه: بدونش،
 * عکسِ تازه از دادهٔ **قدیمیِ** همون لحظه گرفته می‌شد و رفرشِ کاربر عملاً هیچ
 * کاری نمی‌کرد.
 */
export interface FavoriteFreeze {
  snap: FavoriteSnapshot;
  token: unknown;
  armed: boolean;
}

/** حالتِ اولیه: خالی و منتظرِ اولین دادهٔ نشسته (یعنی همون «موقعِ ورود»). */
export const initialFreeze = (token: unknown = 0): FavoriteFreeze => (
  { snap: NO_FAVORITES, token, armed: true }
);

/**
 * یه قدم جلو بردنِ حالت.
 *
 * `settled` یعنی دادهٔ نشان‌شده‌ها از سرور رسیده و همین الان در حالِ گرفتن
 * نیست. `token` هر چیزیه که با هر «ورودِ تازه به صفحه» یا رفرشِ دستی عوض
 * می‌شه.
 *
 * عوض شدنِ خودِ `live` هیچ‌وقت به‌تنهایی عکس رو تازه نمی‌کنه — و همین‌جاست که
 * زدنِ قلب دیگه فهرست رو تکون نمی‌ده.
 */
export function nextFreeze(
  prev: FavoriteFreeze, live: FavoriteSnapshot, settled: boolean, token: unknown,
): FavoriteFreeze {
  const armed = prev.armed || !Object.is(token, prev.token);
  if (armed && settled) return { snap: new Set(live), token, armed: false };
  // بدونِ این، هر رندر یه آبجکتِ تازه می‌ساخت و مقایسه‌های پایین‌دست بی‌فایده
  // می‌شدن.
  if (armed === prev.armed && Object.is(token, prev.token)) return prev;
  return { snap: prev.snap, token, armed };
}

/**
 * همون منطق، به شکلِ هوک — چیزی که صفحه واقعاً صدا می‌زنه.
 *
 * عمداً `useRef` و نه `useState`: عکسِ تازه باید توی **همون** رندر اثر کنه،
 * وگرنه فهرست یک‌بار با ترتیبِ قدیمی کشیده می‌شه و بعد می‌پره — دقیقاً همون
 * پرشی که این تسک برای برداشتنش نوشته شد. نوشتن روی ref موقعِ رندر اینجا امنه
 * چون `nextFreeze` خالصه: با ورودیِ یکسان همون خروجی، پس رندرِ دوباره (مثلاً
 * StrictMode) چیزی رو خراب نمی‌کنه.
 */
export function useFavoriteSnapshot(
  live: FavoriteSnapshot, settled: boolean, token: unknown = 0,
): FavoriteSnapshot {
  const ref = useRef<FavoriteFreeze>(initialFreeze(token));
  ref.current = nextFreeze(ref.current, live, settled, token);
  return ref.current.snap;
}
